import { strFromU8, unzipSync } from 'fflate'
import { DB_VERSION } from '@/src/renderer/lib/db/models'
import {
  BACKUP_FORMAT, BACKUP_FORMAT_VERSION, CONTENT_TABLES, DATE_FIELDS, emptyTables, NORMALIZERS, SETTINGS_TABLES,
  type BackupCryptoParams, type BackupData, type BackupManifest, type BackupProfile, type BackupTable, type BackupTables,
} from './format'

/**
 * Warum sich eine Datei nicht lesen lässt: `invalid` — kein HappyTask-Backup (fremdes Format,
 * kaputtes ZIP); `newer` — aus einer neueren App-Version; `encrypted` — mit Passwort geschützt.
 */
export type BackupErrorCode = 'invalid' | 'newer' | 'encrypted'

export class BackupFileError extends Error {
  constructor(readonly code: BackupErrorCode, message?: string) {
    super(message ?? code)
    this.name = 'BackupFileError'
  }
}

export interface UnpackedBackup {
  manifest: BackupManifest
  data: BackupData
}

function unzip(bytes: Uint8Array): Record<string, Uint8Array> {
  try {
    return unzipSync(bytes)
  } catch {
    throw new BackupFileError('invalid', 'not a zip file')
  }
}

function parseJson(file: Uint8Array | undefined, name: string): any {
  if (!file) throw new BackupFileError('invalid', `${name} missing`)
  try {
    return JSON.parse(strFromU8(file))
  } catch {
    throw new BackupFileError('invalid', `${name} is not JSON`)
  }
}

/**
 * Liest nur das Manifest und prüft die Versionen: `formatVersion` oder `dbVersion` neuer als die
 * App → `newer`; fremdes Format → `invalid`.
 */
export function readBackupManifest(bytes: Uint8Array): BackupManifest {
  const manifest = parseJson(unzip(bytes)['manifest.json'], 'manifest.json')
  if (manifest?.format !== BACKUP_FORMAT || typeof manifest.formatVersion !== 'number') {
    throw new BackupFileError('invalid', 'unknown format')
  }
  if (manifest.formatVersion > BACKUP_FORMAT_VERSION || (manifest.dbVersion ?? 0) > DB_VERSION) {
    throw new BackupFileError('newer')
  }
  return manifest as BackupManifest
}

/**
 * Entpackt ein Backup: Versionen prüfen, Datums-Felder je Tabelle zurückverwandeln, fehlende Felder
 * eines älteren Backups auffüllen.
 */
export function unpackBackup(bytes: Uint8Array): UnpackedBackup {
  const files = unzip(bytes)
  const manifest = readBackupManifest(bytes)
  if (manifest.encrypted) throw new BackupFileError('encrypted')
  return readData(manifest, parseJson(files['data.json'], 'data.json'))
}

/** Ein geschütztes Backup: das Manifest und die verschlüsselten Daten, die der Hauptprozess öffnet. */
export interface ProtectedBackup {
  manifest: BackupManifest & { crypto: BackupCryptoParams }
  data: Uint8Array
}

/** Liest ein geschütztes Backup bis vor das Entschlüsseln; ein offenes oder kaputtes → `invalid`. */
export function readProtectedBackup(bytes: Uint8Array): ProtectedBackup {
  const files = unzip(bytes)
  const manifest = readBackupManifest(bytes)
  const data = files['data.enc']
  if (!manifest.encrypted || !manifest.crypto || typeof manifest.crypto !== 'object' || !data) {
    throw new BackupFileError('invalid', 'not a protected backup')
  }
  return { manifest: manifest as ProtectedBackup['manifest'], data }
}

/** Die entschlüsselten Daten eines geschützten Backups — weiter wie bei einem offenen. */
export function unpackDecrypted(manifest: BackupManifest, plain: Uint8Array): UnpackedBackup {
  return readData(manifest, parseJson(plain, 'data.enc'))
}

function readData(manifest: BackupManifest, raw: any): UnpackedBackup {
  if (!raw || typeof raw !== 'object' || typeof raw.tables !== 'object' || raw.tables === null) {
    throw new BackupFileError('invalid', 'data.json has no tables')
  }
  return {
    manifest,
    data: {
      accountId: typeof raw.accountId === 'string' ? raw.accountId : null,
      profile: readProfile(raw.profile),
      tables: readTables(raw.tables),
    },
  }
}

function readTables(raw: Record<string, unknown>): BackupTables {
  const tables = emptyTables()
  for (const table of [...CONTENT_TABLES, ...SETTINGS_TABLES] as BackupTable[]) {
    const rows = raw[table]
    if (!Array.isArray(rows)) continue
    const normalize = NORMALIZERS[table] as (row: any) => any
    ;(tables[table] as unknown[]) = rows
      .filter(row => row && typeof row === 'object' && typeof (row as { id?: unknown }).id === 'string')
      .map(row => normalize(reviveDates(row as Record<string, unknown>, DATE_FIELDS[table])))
  }
  return tables
}

function reviveDates(row: Record<string, unknown>, fields: readonly string[]): Record<string, unknown> {
  const out = { ...row }
  for (const field of fields) {
    const value = out[field]
    if (typeof value === 'string') {
      const date = new Date(value)
      out[field] = Number.isNaN(date.getTime()) ? null : date
    }
  }
  return out
}

function readProfile(raw: any): BackupProfile | null {
  if (!raw || typeof raw !== 'object') return null
  const text = (value: unknown) => (typeof value === 'string' ? value : null)
  return { fullName: text(raw.fullName), displayName: text(raw.displayName), email: text(raw.email), avatarPresetId: text(raw.avatarPresetId) }
}
